import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WarningIcon from '@mui/icons-material/Warning';
import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { useMemo } from 'react';

import type { IInventoryItem } from '@ekhodealer/ekho-common';
import type { CategorizedSyncItems } from '../../hooks/useSyncSelectedInventory';

import { queries } from '@config/server/queryKeys';

import { useGetInventoryItems } from '../../api/useGetInventoryItems';
import {
	formatCooldownRemaining,
	getSyncCooldownRemaining,
	type SyncStatusCache,
} from '../../api/useSyncInventory';

import { ActionConfirmationModal, type CategoryRow } from './ActionConfirmationModal';

interface SyncConfirmationModalProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	selectedIds: Set<string>;
	totalSelected: number;
	onConfirm: (readyItems: IInventoryItem[]) => void;
	onCancel: () => void;
	/** Number of items sync was initiated for (0 means not yet confirmed) */
	syncInitiatedCount: number;
	/** Whether the sync operation is currently pending */
	isSyncing?: boolean;
}

/**
 * Categorize items into ready, missingUrl, and onCooldown groups
 */
const categorizeSyncItems = (
	items: UseQueryResult<IInventoryItem, Error>[],
	queryClient: ReturnType<typeof useQueryClient>,
): CategorizedSyncItems => {
	const result: CategorizedSyncItems = {
		ready: [],
		missingUrl: [],
		onCooldown: [],
	};

	for (const queryResult of items) {
		if (queryResult.error || !queryResult.data) {
			continue;
		}

		const item = queryResult.data as IInventoryItem;

		// Check for vehicleDetailsUrl or scrapeUrl (required for syncing)
		const vehicleDetailsUrl =
			item.vehicleDetailsUrl || item.scrapingConfig?.INVENTORY_ITEM_SYNC?.scrapeUrl;
		if (!vehicleDetailsUrl) {
			result.missingUrl.push(item);
			continue;
		}

		// Check for cooldown (sync status cache is fine to read directly)
		const syncStatus = queryClient.getQueryData<SyncStatusCache>(
			queries.inventory.syncStatus(item.id).queryKey,
		);
		const remainingMs = getSyncCooldownRemaining(syncStatus);

		if (remainingMs > 0) {
			result.onCooldown.push({ item, remainingMs });
			continue;
		}

		// Item is ready to sync
		result.ready.push(item);
	}

	return result;
};

/**
 * Modal for confirming sync operation on selected inventory items
 * Fetches items using query hooks and categorizes them
 * Shows categorized items: ready to sync, missing URL, and on cooldown
 * After confirmation, shows immediate success message (fire-and-forget)
 */
export const SyncConfirmationModal = ({
	isOpen,
	onOpenChange,
	selectedIds,
	totalSelected,
	onConfirm,
	onCancel,
	syncInitiatedCount,
	isSyncing = false,
}: SyncConfirmationModalProps) => {
	const queryClient = useQueryClient();

	// Fetch all selected items using query hooks
	const itemQueries = useGetInventoryItems(selectedIds, isOpen && selectedIds.size > 0);

	// Categorize items after fetching
	const categorizedItems = useMemo(() => {
		if (!isOpen || selectedIds.size === 0) {
			return { ready: [], missingUrl: [], onCooldown: [] };
		}
		return categorizeSyncItems(itemQueries, queryClient);
	}, [isOpen, selectedIds.size, itemQueries, queryClient]);

	const { ready, missingUrl, onCooldown } = categorizedItems;

	// Check if we're still loading
	const isLoading = itemQueries.some((query) => query.isPending);

	// Calculate max remaining cooldown for display
	const maxCooldownMs =
		onCooldown.length > 0 ? Math.max(...onCooldown.map((c) => c.remainingMs)) : 0;

	// Build category rows for the generic modal
	const categories: CategoryRow[] = [
		{
			icon: <CheckCircleIcon />,
			iconColor: 'var(--color-positive, #22c55e)',
			count: ready.length,
			message: ready.length === 1 ? 'is ready to sync' : 'are ready to sync',
		},
		{
			icon: <WarningIcon />,
			iconColor: 'var(--color-warning, #f59e0b)',
			count: missingUrl.length,
			message:
				missingUrl.length === 1
					? "has missing information and can't be synced yet"
					: "have missing information and can't be synced yet",
		},
		{
			icon: <AccessTimeIcon />,
			iconColor: 'var(--color-secondary, #6b7280)',
			count: onCooldown.length,
			message: `synced recently (try again in ${formatCooldownRemaining(maxCooldownMs)})`,
		},
	];

	const handleConfirm = () => {
		onConfirm(ready);
	};

	return (
		<ActionConfirmationModal
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			categories={categories}
			totalSelected={totalSelected}
			readyCount={ready.length}
			onConfirm={handleConfirm}
			onCancel={onCancel}
			actionInitiatedCount={syncInitiatedCount}
			isConfirming={isSyncing || isLoading}
			title='Review before syncing'
			subtitle='You selected {count} inventory item{plural}'
			successTitle='Sync started'
			successIcon={<RocketLaunchIcon />}
			successMessage='sync job{plural} started'
			successDescription='Updates are processing in the background. Check back in a few minutes to see the latest inventory data.'
			confirmButtonText='Sync {count} now'
			noItemsMessage='No items are currently available to sync.'
		/>
	);
};
